import {makeStyles, TableCell, TableRow} from "@material-ui/core";
import {Delete as DeleteIcon} from "@material-ui/icons";
import moment from "libs/moment";
import PropTypes from "prop-types";
import React, {useReducer, useState} from "react";
import DateFormats from "constants/DateFormats";
import WorkableIconButton from "components/button/WorkableIconButton";
import OffersChoiceResultService from "services/OffersChoiceResultService";
import {OfferChoiceResultFileState, OfferChoiceResultFileStateLabel} from "../constants/OfferChoiceResultFileState";
import WorkableIconUnpublishButton from "components/button/WorkableIconUnpublishButton";
import WorkableIconPublishButton from "components/button/WorkableIconPublishButton";
import WorkableIconDownloadCountButton from "components/button/WorkableIconDownloadCountButton";
import iconWorksReducer from "libs/iconWorksReducer";
import WorkableIconDownloadButton from "components/button/WorkableIconDownloadButton";
import DownloadHistoryPopup, {createDownloadHistoryFile} from "components/popup/DownloadHistoryPopup";
import useSnackbar from "hooks/useSnackbar";
import ShareSelectPopup, {sharePopupData} from "components/popup/ShareSelectPopup";
import Tr from "translations/Translations";
import WorkableIconShareButton from "components/button/WorkableIconShareButton";


const useStyles = makeStyles(theme => ({
    buttonCell: {
        padding: 0
    }
}));

function OfferChoiceResultFilesTableRow(
    {
        demandId,
        editable,
        working,
        offerChoiceResultItem,
        onDeleteFile,
        onUpdateFile,
        file,
        showShare,
        canDownloadFile
    }) {
    const classes = useStyles();
    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        download: false,
        publish: false,
        delete: false,
        share: false,
    });
    const [downloadHistory, setDownloadHistory] = useState(null);
    const [shareFilePopup, setShareFilePopup] = useState(null);
    const {enqueueSuccessSnackbar} = useSnackbar();

    const onDownloadCount = () => {
        setDownloadHistory(createDownloadHistoryFile(file.name, demandId, file.identity));
    };

    const onDownloadDialogClose = () => {
        setDownloadHistory(null);
    };

    const downloadFile = (file) => {
        dispatchIconWorks({type: 'download', works: true})
        OffersChoiceResultService.getDownloadFile(demandId, offerChoiceResultItem.identity, file.identity)
            .catch(() => {
            }).finally(() => dispatchIconWorks({type: 'download', works: false}));
    };

    const deleteFile = (file) => {
        dispatchIconWorks({type: 'delete', works: true})
        OffersChoiceResultService.deleteFile(demandId, offerChoiceResultItem.identity, file.identity)
            .then(() => {
                onDeleteFile(file)
            })
            .catch(() => dispatchIconWorks({type: 'delete', works: false}));
    };

    const publishFile = (file) => {
        dispatchIconWorks({type: 'publish', works: true});
        const promise = OffersChoiceResultService.publishFile(demandId, offerChoiceResultItem.identity, file.identity);
        doPublishChange(promise);
    };

    const unpublishFile = (file) => {
        dispatchIconWorks({type: 'publish', works: true});
        const promise = OffersChoiceResultService.unpublishFile(demandId, offerChoiceResultItem.identity, file.identity);
        doPublishChange(promise);
    };

    const doPublishChange = (promise) => {
        promise
            .then((resp) => {
                if (resp?.file) {
                    onUpdateFile(resp.file);
                }
                if (resp?.info) {
                    OffersChoiceResultService.setChoiceResult(resp.info)
                }
            })
            .finally(() => dispatchIconWorks({type: 'publish', works: false}));
    };


    const onShareFile = (row) => setShareFilePopup(sharePopupData(row.name, row));

    const doShare = (row, system) => {
        setShareFilePopup(null);
        dispatchIconWorks({type: 'share', works: true});
        OffersChoiceResultService.shareFile(demandId, offerChoiceResultItem.identity, row.identity, system).then((resp) => {
            if (resp)
                enqueueSuccessSnackbar(Tr("int_upload_success_x", {x: system}, true))
        }).finally(() => {
            dispatchIconWorks({type: 'share', works: false})
        });
    }

    return (
        <TableRow>
            <TableCell>{file.name}</TableCell>
            <TableCell>{OfferChoiceResultFileStateLabel(file.state)}</TableCell>
            <TableCell>{file.datePublished ? moment(file.datePublished).format(DateFormats.DATETIME_FORMAT) : null}</TableCell>
            <TableCell className={classes.buttonCell}>
                <WorkableIconDownloadButton disabled={iconWorks.anyWorks || !canDownloadFile} working={iconWorks.download} onClick={() => downloadFile(file)}/>
            </TableCell>
            {
                showShare &&
                <TableCell className={classes.buttonCell}>
                    <WorkableIconShareButton
                        onClick={() => onShareFile(file)}
                        disabled={iconWorks.anyWorks}
                        working={iconWorks.share}
                    />

                    {!!shareFilePopup && <ShareSelectPopup
                        data={shareFilePopup}
                        onCancel={() => setShareFilePopup(null)}
                        onChoose={doShare}
                    />}
                </TableCell>
            }
            <TableCell className={classes.buttonCell}>
                {
                    file.state === OfferChoiceResultFileState.PUBLISHED ?
                        <WorkableIconUnpublishButton
                            disabled={iconWorks.anyWorks || !editable}
                            working={iconWorks.publish}
                            onClick={() => unpublishFile(file)}
                        /> :
                        <WorkableIconPublishButton
                            disabled={iconWorks.anyWorks || !editable}
                            working={iconWorks.publish}
                            onClick={() => publishFile(file)}
                        />
                }
            </TableCell>
            <TableCell className={classes.buttonCell}>
                <WorkableIconDownloadCountButton onClick={onDownloadCount}/>
                {downloadHistory &&
                <DownloadHistoryPopup
                    downloadHistoryFile={downloadHistory}
                    service={{
                        getDownloadHistory: async (demandId, fileId, pagination) => {
                            return await OffersChoiceResultService.getDownloadHistory(demandId, offerChoiceResultItem.identity, fileId, pagination)
                        }
                    }}
                    onClose={onDownloadDialogClose}
                />
                }
            </TableCell>
            <TableCell className={classes.buttonCell}>
                <WorkableIconButton
                    disabled={!!file.datePublished || iconWorks.anyWorks || !editable}
                    Icon={DeleteIcon}
                    working={iconWorks.delete}
                    onClick={() => deleteFile(file)}
                />
            </TableCell>
        </TableRow>
    )
}

OfferChoiceResultFilesTableRow.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    editable: PropTypes.bool.isRequired,
    working: PropTypes.bool.isRequired,
    offerChoiceResultItem: PropTypes.object.isRequired,
    onDeleteFile: PropTypes.func.isRequired,
    onUpdateFile: PropTypes.func.isRequired,
    file: PropTypes.object.isRequired,
    showShare: PropTypes.bool.isRequired
}

export default OfferChoiceResultFilesTableRow;

